<!-- Copyright 2025, Common Good Learning Tools LLC -->
<template>
	<k-dialog v-if="local_asset" v-model="asset_loaded_locally" max-width="300px" persistent>
		<k-card>
			<k-card-title>
				<div tabindex="0">
					<div>Reviewers for:</div>
					<b>{{ asset_title }}</b>
				</div>
			</k-card-title>

			<k-card-text style="color: #000;">
				<div v-for="(reviewer, i) in local_asset.reviewers" :key="'reviewer_' + i">
					<div class="d-flex mb-1">
						{{ reviewer.assigned_user_name }}
						
						<k-spacer/>

						<div class="active-icon d-flex">
							<k-tooltip bottom>
								<template v-slot:activator="{on}">
									<k-icon v-on="on" class="mx-1 icon" :color="reviewer.active ? 'green' : 'gray'" x-small @click="toggle_active(reviewer)">
										{{ reviewer.active ? 'fas fa-user-check' : 'fas fa-user'}}
									</k-icon>
								</template>

								{{ reviewer.assigned_user_name }} is {{reviewer.active ? '' : 'not'}} an active reviewer.
							</k-tooltip>

							<k-spacer/>
						</div>

						<k-tooltip bottom>
							<template v-slot:activator="{on}">
								<k-icon v-on="on" class="mx-1 icon" :color="reviewer.approved ? 'green' : 'gray'" x-small>
									{{ reviewer.approved ? 'fas fa-comment-check' : 'fas fa-comment-xmark'}}
								</k-icon>
							</template>

							{{ reviewer.assigned_user_name }} has {{ reviewer.approved ? '' : 'not' }} approved this asset.
						</k-tooltip>
						
						<k-tooltip bottom>
							<template v-slot:activator="{on}">
								<k-icon v-on="on" class="ml-1 icon" color="red" x-small @click="remove_reviewer(reviewer, i)">fas fa-circle-x</k-icon>
							</template>

							Remove {{ reviewer.assigned_user_name }} as a reviewer.
						</k-tooltip>
					</div>
					
					<k-divider v-if="i != local_asset.reviewers.length -1"/>
				</div>
			</k-card-text>

			<k-card-actions class="pl-4 pr-4 pb-4">
				<k-btn color="primary" small @click="find_reviewer_email">Add Reviewer</k-btn>
				<k-spacer/>
				<k-btn color="secondary" small class="mr-1" @click="$emit('done')">
					Done
				</k-btn>
			</k-card-actions>
		</k-card>
	</k-dialog>
</template>

<script>
import { mapState } from 'vuex'

export default {
	name: 'AssetReviewerDialog',

	props: {
		selected_asset: {type: Object, required: true},
		asset_type: {type: String, required: true},
		payment_data: {type: Object, required: false}
	},

	data() {
		return {
			local_asset: null,
			adding_reviewer: null,
			roles: [
				{key: 'author', title: 'Author'},
				{key: 'reviewer', title: 'Reviewer'},
				{key: 'publisher', title: 'Publisher'},
			],
			headers: [
				{ text: 'Amount', align: 'start', sortable: false, width: '25px', value: 'amount'},
				{ text: 'Date', align: 'start', sortable: false, width: '50px', value: 'date'},
				{ text: 'Note', align: 'start', sortable: false, value: 'note'},
				{ text: 'Remove', align: 'center', sortable: false, width: '20px', value: 'remove'},
			],
		}
	},

	computed: {
		...mapState(['user_info', 'site_config']),

		asset_title() {
			return this.local_asset.lesson_title ?? this.local_asset.description
		},

		asset_id() {
			return this.local_asset.lesson_id ?? this.local_asset.resource_id
		},

		asset_loaded_locally() {
			return !! this.local_asset
		},
	},

	mounted() {
		this.local_asset = object_copy(this.selected_asset)
	},

	methods: {
		find_reviewer_email() {
			this.$prompt({
				title: 'Assign Reviewer',
				text: 'Enter user email:',
				promptType: 'autocomplete',
				serviceName: 'email_search',
				acceptText: 'Assign',
			}).then(email => {
				// return value should be e.g. 'pepper@gmail.com (Pepper Williams)'; extract the email
				email = $.trim(email).toLowerCase().replace(/^(\S+).*/, '$1')

				if (empty(email) || email.indexOf('@') == -1) {
					this.$alert('Please enter a valid user email.')
						.then(() => { this.find_reviewer_email() })
				} else {	
					this.validate_email(email)
				}
			}).catch(n => { console.error(n) })
		},

		validate_email(email) {
			let payload = {
				user_id: this.user_info.user_id,
				email: email,
				get_rights: true,
			}
			U.loading_start()
			U.ajax('admin_validate_email', payload, result => {
				U.loading_stop()
				if (result.status != 'ok') {
					console.error('Error in admin ajax call'); vapp.ping(); return;
				}

				if (empty(result.user_id)) {
					this.$alert('We couldn’t find that email address in the system.').then(() => {this.find_reviewer_email()})
				} else {
					this.add_reviewer(result)
				}
			});
		},

		add_reviewer(user) {
			if (this.local_asset.reviewers?.some(r => r.assigned_user_id == user.user_id)) {
				this.$alert(`${user.first_name} ${user.last_name} is already assigned as a reviewer.`)
				return
			}

			let rights_arr = user.admin_rights ?? []
			rights_arr.push('asset_reviewer.' + this.asset_type + '_id.' + this.asset_id)

			let payload = {
				user_id: this.user_info.user_id,
				user_identifier: user.user_id,
				rights_arr,
			}

			U.loading_start()
			U.ajax('admin_update_admin_rights', payload, async result => {
				U.loading_stop()
				if (result.status != 'ok') {
					console.error('Error in admin ajax call'); vapp.ping(); return;
				} else {
					let asset_creator_id = this.asset_type == 'lesson' ? this.local_asset.creator_user_id : this.local_asset.creator
					let active = this.local_asset.reviewers.length ? 0 : 1
					let log_data = {
						user_id: this.user_info.user_id,
						asset_id: this.asset_id,
						asset_type: this.asset_type,
						asset_creator_id,
						action: 'assignment',
						assigned_user_id: user.user_id,
						assigned_user_name: user.first_name + ' ' + user.last_name,
						active,
						approved: 0
					}

					let log_result = await this.save_log(log_data)
					if (log_result.asset_log_entry?.id) log_data.id = log_result.asset_log_entry.id
					if (!this.local_asset.reviewers) this.local_asset.reviewers = []
					this.local_asset.reviewers.push(log_data)
					if (this.payment_data) this.update_payment_data('add', log_data)

					// a newly assigned reviewer means this asset needs another look, even if it was already approved for publication
					let msg = log_data.assigned_user_name + ' assigned to review this asset.'
					if (this.local_asset.publish_status === 'ready_for_publication') {
						await this.revert_to_under_review()
						msg += '<br>Status reverted to “Under Review” because a new reviewer was added.'
					}

					this.$emit('updated', this.local_asset)
					this.$inform(msg)
				}
			});
		},

		async revert_to_under_review() {
			let today = new Date().toJSON().slice(0, 19).replace('T', ' ')
			let from_status = this.local_asset.publish_status

			let asset_data = object_copy(this.local_asset)
			asset_data.publish_status = 'under_review'
			asset_data.status_date = today
			asset_data.agency_sanctioned = false

			let payload = {}
			if (this.asset_type == 'lesson') {
				payload.lesson_class = asset_data.lesson_id === asset_data.template_id ? 'template' : 'teacher'
				payload.lesson_data = asset_data
			} else {
				payload.resource = new Resource(asset_data)
			}

			await this.$store.dispatch('save_' + this.asset_type, payload)

			this.local_asset.publish_status = 'under_review'
			this.local_asset.status_date = today
			this.local_asset.agency_sanctioned = false

			let asset_creator_id = this.asset_type == 'lesson' ? this.local_asset.creator_user_id : this.local_asset.creator

			await this.save_log({
				user_id: this.user_info.user_id,
				asset_id: this.asset_id,
				asset_type: this.asset_type,
				asset_creator_id,
				action: 'status',
				from: from_status,
				to: 'under_review'
			})
		},

		toggle_active(reviewer) {
			reviewer.active = reviewer.active ? 0 : 1
			
			this.save_log(reviewer)
				.then(() => {
					this.$emit('updated', this.local_asset)
				})
		},

		remove_reviewer(reviewer, index) {
			if (this.reviewer_has_payments(reviewer)) {
				this.$inform({text: 'This reviewer cannot be removed because payments have been recorded for them.'})
			} else {
				this.$confirm({
					title: 'Are you sure?',
					text: 'Are you sure you want to remove this reviewer?',
					acceptText: 'Remove Reviewer',
					acceptColor: 'red',
					dialogMaxWidth: 600
				}).then(() => {
					let log_data = object_copy(reviewer)
					delete log_data.id
					log_data.active = 0
					log_data.action = 'unassigned'
					log_data.user_id = this.user_info.user_id

					this.save_log(log_data)
						.then(() => {
							this.local_asset.reviewers.splice(index, 1)
							if (this.payment_data) this.update_payment_data('remove', reviewer)
							this.$emit('updated', this.local_asset)
							this.$inform(reviewer.assigned_user_name + ' unassigned.')
						})
				}).catch(() => {})
			}
		},

		reviewer_has_payments(reviewer) {
			if (!this.payment_data || !this.payment_data.reviewer_data) return false

			let reviewer_payment_data = this.payment_data.reviewer_data.filter(x => x.user_id == reviewer.assigned_user_id)

			if (!reviewer_payment_data.length) return false

			return !!reviewer_payment_data[0].payments?.length
		},

		update_payment_data(action, reviewer) {
			let payment_data = object_copy(this.payment_data)
			if (action === 'add') {
				let new_reviewer = {
					user_id: reviewer.assigned_user_id,
					user_name: reviewer.assigned_user_name,
					approved_date: ''
				}

				if (!payment_data.reviewer_data) payment_data.reviewer_data = []
				payment_data.reviewer_data.push(new_reviewer)
			} else if (payment_data.reviewer_data) {
				payment_data.reviewer_data = payment_data.reviewer_data.filter(x => x.user_id != reviewer.assigned_user_id)
			}

			this.$emit('payment_updated', payment_data)
		},

		save_log(log_data) {
			let log_payload = {
				user_id: this.user_info.user_id,
				log_data
			}
			
			return this.$store.dispatch('save_asset_log', log_payload)
		},
	},
}
</script>

<style lang="scss" scoped>
.icon {
	width: 15px;
}

.active-icon {
	width: 20px;
}
</style>
